/**
 * Per-step field descriptors for calculator form analytics.
 *
 * Answers "which fields does an advisor fill in vs skip on each step?" without
 * ever reading a field VALUE off the page — every descriptor exposes only:
 *   - `visible`  : is the field shown for the current product / tenant / inputs
 *   - `filled`   : does it currently hold a value
 *   - `changed`  : does it differ from the snapshot taken when the form started
 *                  (proxy for "the advisor actually engaged with this field",
 *                   since almost every field is pre-filled with a default)
 *
 * Getters read the demand snapshot defensively (optional chaining, never throw).
 * Visibility reuses store getters / tenant config where possible; conditional
 * fields whose predicate lives only in a component template are approximated
 * conservatively and flagged `conditional`.
 *
 * Scope note: step 3 (INCOME) is intentionally not covered at field level — its
 * inputs are highly conditional on the selected income type (7 distinct forms).
 * It is still tracked at the funnel level (step_viewed / step_completed). Full
 * per-income-type field coverage is a follow-up.
 */
import { FormSteps } from '@/components/calculator/FormSteps';
import EnvironmentService from '@/env/EnvironmentService';
import { isEqual } from 'lodash-es';

// The demand model is a union (mortgage / cash) with many optional + conditional
// members, so field access is intentionally loose and defensive.
type Demand = any;
type SimulationStoreLike = any;

type FieldCtx = {
  input: Demand;
  household: Demand;
  borrower: Demand;
  store: SimulationStoreLike;
  isLendi: boolean;
};

type FieldDescriptor = {
  name: string;
  optional?: boolean;
  conditional?: boolean;
  visible?: (ctx: FieldCtx) => boolean;
  value: (ctx: FieldCtx) => unknown;
};

export type FieldStat = {
  name: string;
  optional: boolean;
  conditional: boolean;
  visible: boolean;
  filled: boolean;
  changed: boolean;
};

export type StepFieldStats = {
  fields: FieldStat[];
  visibleCount: number;
  engagedCount: number;
};

const calculatorConfig = () => EnvironmentService.Environment.getAppDomainConfig().calculator;

const mortgageStep1: FieldDescriptor[] = [
  { name: 'loanPurpose.activity', value: (c) => c.input?.loanPurpose?.activity, },
  { name: 'collateral', value: (c) => c.input?.collateral, },
  { name: 'loanAmount', value: (c) => c.input?.loanAmount, },
  { name: 'hypothecValue', value: (c) => c.input?.hypothecValue, },
  { name: 'loanPeriod', value: (c) => c.input?.loanPeriodInMonths ?? c.input?.loanPeriod, },
  { name: 'currency', optional: true, value: (c) => c.input?.currency, },
  { name: 'fixedInstallments', optional: true, value: (c) => c.input?.fixedInstallments, },
  {
    name: 'loanPurpose.market',
    conditional: true,
    visible: (c) => calculatorConfig().hasMarketTypes &&
      (!!c.store.isLoanPurposeActivityBuying || !!c.store.isLoanPurposeActivityBuyingAndRenovating),
    value: (c) => c.input?.loanPurpose?.market,
  },
  {
    name: 'firstOrSubsequentRealEstate',
    conditional: true,
    visible: () => calculatorConfig().hasFirstOrSubsequentRealEstateInfo,
    value: (c) => c.input?.firstOrSubsequentRealEstate,
  },
  {
    name: 'constructionPeriod',
    conditional: true,
    visible: (c) => !!c.store.isLoanPurposeActivityBuilding,
    value: (c) => c.input?.constructionPeriod,
  },
  {
    name: 'explicitBridgingPeriod',
    optional: true,
    conditional: true,
    visible: (c) => c.isLendi,
    value: (c) => c.input?.explicitBridgingPeriod,
  },
  { name: 'household.landId', conditional: true, visible: (c) => c.isLendi, value: (c) => c.household?.landId, },
  { name: 'household.districtId', conditional: true, visible: (c) => c.isLendi, value: (c) => c.household?.districtId, },
];

const cashStep1: FieldDescriptor[] = [
  { name: 'loanAmount', value: (c) => c.input?.loanAmount, },
  { name: 'loanPeriod', value: (c) => c.input?.loanPeriod ?? c.input?.loanPeriodInMonths, },
  { name: 'loanTypes.cash', optional: true, value: (c) => c.input?.loanTypes?.cash, },
  { name: 'loanTypes.consolidation', optional: true, value: (c) => c.input?.loanTypes?.consolidation, },
];

// Step 2 (borrowers) — first borrower of the first household. Multi-borrower /
// multi-household depth is a follow-up; most simulations use a single borrower.
const borrowersStep2: FieldDescriptor[] = [
  { name: 'birthDate', value: (c) => c.borrower?.birthDate, },
  { name: 'gender', optional: true, value: (c) => c.borrower?.gender, },
  { name: 'civilStatus', value: (c) => c.borrower?.civilStatus, },
  { name: 'origin', value: (c) => c.borrower?.origin, },
  { name: 'resident', optional: true, value: (c) => c.borrower?.resident, },
  { name: 'professionId', optional: true, value: (c) => c.borrower?.professionId, },
  { name: 'mainIncome.incomeTypeId', value: (c) => c.borrower?.mainIncome?.incomeTypeId, },
  { name: 'mainIncome.duration', value: (c) => c.borrower?.mainIncome?.duration, },
  { name: 'hasAdditionalIncomeSource', optional: true, value: (c) => c.borrower?.hasAdditionalIncomeSource, },
  {
    name: 'additionalIncome.incomeTypeId',
    conditional: true,
    visible: (c) => !!c.borrower?.hasAdditionalIncomeSource,
    value: (c) => c.borrower?.additionalIncome?.incomeTypeId,
  },
];

// Step 4 (costs & liabilities) — first household. Shared across products.
const costsStep4: FieldDescriptor[] = [
  { name: 'averageCosts.life', optional: true, conditional: true, visible: (c) => c.isLendi, value: (c) => c.household?.averageCosts?.life, },
  { name: 'averageCosts.house', optional: true, conditional: true, visible: (c) => c.isLendi, value: (c) => c.household?.averageCosts?.house, },
  { name: 'averageCosts.car', optional: true, conditional: true, visible: (c) => c.isLendi, value: (c) => c.household?.averageCosts?.car, },
  { name: 'averageCosts.other', optional: true, conditional: true, visible: (c) => c.isLendi, value: (c) => c.household?.averageCosts?.other, },
  { name: 'hasLiabilities', optional: true, value: (c) => c.household?.hasLiabilities, },
  {
    name: 'liabilities.installmentsPLN',
    optional: true,
    conditional: true,
    visible: (c) => !!c.household?.hasLiabilities,
    value: (c) => c.household?.liabilities?.installmentsPLN,
  },
  {
    name: 'liabilities.accountLimit',
    optional: true,
    conditional: true,
    visible: (c) => !!c.household?.hasLiabilities,
    value: (c) => c.household?.liabilities?.accountLimit,
  },
  {
    name: 'liabilities.cardsLimit',
    optional: true,
    conditional: true,
    visible: (c) => !!c.household?.hasLiabilities,
    value: (c) => c.household?.liabilities?.cardsLimit,
  },
  {
    name: 'liabilities.alimony',
    optional: true,
    conditional: true,
    visible: (c) => !!c.household?.hasLiabilities,
    value: (c) => c.household?.liabilities?.alimony,
  },
];

// Steps covered at field level (INCOME / step 3 is funnel-only — see file docs).
const FIELD_TRACKED_STEPS: FormSteps[] = [FormSteps.CREDIT_PARAMETERS, FormSteps.BORROWERS, FormSteps.LIABILITIES];

const registryFor = (step: number, isCash: boolean): FieldDescriptor[] => {
  switch (step) {
    case FormSteps.CREDIT_PARAMETERS:
      return isCash ? cashStep1 : mortgageStep1;
    case FormSteps.BORROWERS:
      return borrowersStep2;
    case FormSteps.LIABILITIES:
      return costsStep4;
    default:
      return [];
  }
};

const buildCtx = (input: Demand, store: SimulationStoreLike, isLendi: boolean): FieldCtx => ({
  input,
  household: input?.households?.[0] ?? null,
  borrower: input?.households?.[0]?.borrowers?.[0] ?? null,
  store,
  isLendi,
});

const isFilled = (value: unknown): boolean => value !== null && value !== undefined && value !== '';

const safeValue = (fn: (ctx: FieldCtx) => unknown, ctx: FieldCtx): unknown => {
  try {
    return fn(ctx);
  } catch {
    return undefined;
  }
};

const safeVisible = (fn: ((ctx: FieldCtx) => boolean) | undefined, ctx: FieldCtx): boolean => {
  if (!fn) {
    return true;
  }
  try {
    return !!fn(ctx);
  } catch {
    return false;
  }
};

/**
 * Compute per-field stats for a step against the current store snapshot.
 * `initialInput` is the deep-cloned demand captured when the form started; when
 * absent, `changed` is reported as false.
 */
export const getStepFieldStats = (
  step: number,
  store: SimulationStoreLike,
  initialInput: Demand,
): StepFieldStats => {
  const isLendi = EnvironmentService.Environment.isAppDomain().LENDI_PL;
  const descriptors = registryFor(step, !!store?.isCash);
  const current = buildCtx(store?.userInput, store, isLendi);
  const initial = initialInput ? buildCtx(initialInput, store, isLendi) : null;

  const fields: FieldStat[] = descriptors.map((descriptor) => {
    const visible = safeVisible(descriptor.visible, current);
    const currentValue = safeValue(descriptor.value, current);
    const initialValue = initial ? safeValue(descriptor.value, initial) : undefined;
    return {
      name: descriptor.name,
      optional: !!descriptor.optional,
      conditional: !!descriptor.conditional,
      visible,
      filled: isFilled(currentValue),
      changed: initial ? !isEqual(currentValue, initialValue) : false,
    };
  });

  const visibleFields = fields.filter((field) => field.visible);
  return {
    fields,
    visibleCount: visibleFields.length,
    engagedCount: visibleFields.filter((field) => field.changed).length,
  };
};

/**
 * Whole-form completeness across the field-tracked steps — attached to
 * `calculator_offers_requested` so form engagement can be correlated with
 * comparator filter activity (joined by simulationId).
 */
export const getFormFieldSummary = (
  store: SimulationStoreLike,
  initialInput: Demand,
): { visibleCount: number; engagedCount: number } => {
  let visibleCount = 0;
  let engagedCount = 0;
  FIELD_TRACKED_STEPS.forEach((step) => {
    const stats = getStepFieldStats(step, store, initialInput);
    visibleCount += stats.visibleCount;
    engagedCount += stats.engagedCount;
  });
  return { visibleCount, engagedCount, };
};
